import {
  ACCESS_TYPES_RULE,
  AI_POLICY_ACCESS_TYPES,
  getAccessTypes,
  getLiveRuleOptions,
  getPortsText,
  hasSensorAndPlatformRules,
  NETWORK_DESTINATION_RULE,
  toFormDestinations,
  WHOLE_WORKLOAD_ACCESS_TYPES,
  WHOLE_WORKLOAD_RULE
} from "./useLiveAiPolicies.selector"

import type {
  AiPolicyKind,
  AiPolicyNetworkDestination
} from "@/api/useAiPolicies.types"
import type { TFunction } from "@/hooks/useTranslation"
import type {
  Record as PolicyListRecord,
  PolicyRecord
} from "@/pages/GovernancePage/applications/policies/getDemoData.types"
import type {
  AiPolicyFormDestination,
  AiPolicyFormRule,
  AiPolicyFormRuleGroup
} from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/AiPolicyForm/AiPolicyForm.types"
import type {
  LiveRuleOption,
  LiveRuleOptionGroup
} from "./useLiveAiPolicies.types"

// What the Kodem sensor enforces in-host: a tool name and the access types
// or network destinations it may not reach, one of them at least. Picked, it
// adds the tool name; they follow, each once
export const TOOL_ACCESS_RULE = "toolAccess"
const TOOL_NAMES = "toolNames"

const ARGUMENTS = "arguments"
// The arguments condition as the rule cards name it
const TOOL_ARGUMENTS = "toolArguments"

const SENSOR_GROUP = "agentEnvironments"

const RULE_GROUPS = [
  {
    title: SENSOR_GROUP,
    ids: [
      TOOL_ACCESS_RULE,
      WHOLE_WORKLOAD_RULE,
      ACCESS_TYPES_RULE,
      NETWORK_DESTINATION_RULE
    ]
  },
  {
    title: "toolCallInspection",
    ids: [TOOL_NAMES, "toolCategories", "toolSources", ARGUMENTS]
  },
  { title: "guardrails", ids: ["promptInjection", "pii", "secrets"] },
  { title: "integrations", ids: ["connectors"] }
]

// The conditions a policy's rules can set, in their groups. An MCP server
// policy's are only the Kodem sensor's: it blocks the server's tools in the
// server's own workload, where no agent platform sees the call
export const getLiveRuleGroups = (
  t: TFunction,
  options: Parameters<typeof getLiveRuleOptions>[1],
  kind: AiPolicyKind = "agent"
): LiveRuleOptionGroup[] => {
  const accessTypes: LiveRuleOption = {
    id: ACCESS_TYPES_RULE,
    type: "single",
    displayName: t("governance.ai.form.rules.accessTypes"),
    scope: "only",
    options: AI_POLICY_ACCESS_TYPES.map((type) => ({
      label: t(`governance.ai.live.form.accessTypes.options.${type}`),
      value: type
    }))
  }
  const toolAccess: LiveRuleOption = {
    id: TOOL_ACCESS_RULE,
    type: "single",
    displayName: t("governance.ai.form.rules.toolAccess"),
    options: []
  }
  const wholeWorkload: LiveRuleOption = {
    id: WHOLE_WORKLOAD_RULE,
    type: "single",
    displayName: t("governance.ai.form.rules.wholeWorkload"),
    options: []
  }
  const networkDestination: LiveRuleOption = {
    id: NETWORK_DESTINATION_RULE,
    type: "single",
    displayName: t("governance.ai.form.rules.networkDestination"),
    scope: "only",
    input: "destinations",
    options: []
  }
  const byId = new Map<string, LiveRuleOption>(
    getLiveRuleOptions(t, options)
      .flatMap((group) => group.options)
      .map((option) => [option.id, option])
  )
  const argumentsOption = byId.get(ARGUMENTS)

  byId.set(ACCESS_TYPES_RULE, accessTypes)
  byId.set(TOOL_ACCESS_RULE, toolAccess)
  byId.set(WHOLE_WORKLOAD_RULE, wholeWorkload)
  byId.set(NETWORK_DESTINATION_RULE, networkDestination)
  if (argumentsOption)
    byId.set(ARGUMENTS, {
      ...argumentsOption,
      displayName: t(`governance.ai.form.rules.${TOOL_ARGUMENTS}`)
    })

  const groups =
    kind === "mcpServer"
      ? RULE_GROUPS.filter(({ title }) => title === SENSOR_GROUP)
      : RULE_GROUPS

  return groups.map(({ title, ids }) => ({
    displayName: t(`governance.ai.live.ruleGroups.${title}`),
    title,
    options: ids.flatMap((id) => byId.get(id) ?? [])
  }))
}

// An MCP server policy offers no plain tool name, but its Tool Access cards
// take one: their row reads it here, offering the servers' tools
export const getMcpToolNamesOption = (
  t: TFunction,
  tools: LiveRuleOption["options"]
): LiveRuleOption => ({
  id: TOOL_NAMES,
  type: "single",
  displayName: t(`governance.ai.form.rules.${TOOL_NAMES}`),
  scope: "matches",
  input: "tags",
  options: tools
})

const isAccessTypesRule = (rule: AiPolicyFormRule | undefined) =>
  rule?.condition === ACCESS_TYPES_RULE

const isDestinationRule = (rule: AiPolicyFormRule | undefined) =>
  rule?.condition === NETWORK_DESTINATION_RULE

const hasRule = (rules: (AiPolicyFormRule | undefined)[], condition: string) =>
  rules.some((rule) => rule?.condition === condition)

// The tool name row of a Tool Access card
export const isToolAccessRow = (rule: AiPolicyFormRule | undefined) =>
  rule?.condition === TOOL_NAMES && !!rule.toolAccess

// The rows of a Tool Access card that go with its tool name
export const isToolAccessPart = (rule: AiPolicyFormRule | undefined) =>
  isToolAccessRow(rule) || isAccessTypesRule(rule) || isDestinationRule(rule)

// The row Tool Access adds in its place
export const toolAccessRow = (): AiPolicyFormRule => ({
  condition: TOOL_NAMES,
  toolAccess: true
})

// The Workload Access row of a card
export const isWholeWorkloadRow = (rule: AiPolicyFormRule | undefined) =>
  rule?.condition === WHOLE_WORKLOAD_RULE

// The rows Workload Access adds, in their order: its access types, network
// alone for now, and its network destinations, required
export const wholeWorkloadRules = (): AiPolicyFormRule[] => [
  { condition: WHOLE_WORKLOAD_RULE },
  { condition: ACCESS_TYPES_RULE, value: [...WHOLE_WORKLOAD_ACCESS_TYPES] },
  { condition: NETWORK_DESTINATION_RULE }
]

// Whether a card offers a condition. Tool Access starts with its own tool
// name, then takes the access types and the network destinations, each once.
// Workload Access starts an empty card and takes only those two, each once
export const isOfferedInCard = (
  id: string,
  card: (AiPolicyFormRule | undefined)[]
) => {
  const hasToolAccess = card.some(isToolAccessRow)

  if (card.some(isWholeWorkloadRow))
    return (
      (id === ACCESS_TYPES_RULE || id === NETWORK_DESTINATION_RULE) &&
      !hasRule(card, id)
    )
  if (id === WHOLE_WORKLOAD_RULE) return !card.some((rule) => rule?.condition)
  if (id === TOOL_ACCESS_RULE) return !hasRule(card, TOOL_NAMES)
  if (id === TOOL_NAMES) return !hasToolAccess
  if (id === ACCESS_TYPES_RULE || id === NETWORK_DESTINATION_RULE)
    return hasToolAccess && !hasRule(card, id)

  return true
}

// Network destinations narrow their card's network access type: access types
// that leave it out leave them nothing to narrow
export const hasDestinationsOffNetwork = (
  groups: AiPolicyFormRuleGroup[] | undefined
) =>
  (groups ?? []).some((group) => {
    const rules = group?.rules ?? []
    const accessTypes = getAccessTypes(rules)

    return (
      rules.some(isDestinationRule) &&
      accessTypes.length > 0 &&
      !accessTypes.includes("network")
    )
  })

// A card with destinations, which the Kodem sensor enforces, and a condition
// only an agent platform can check, at the call
export const hasSensorAndPlatformConditions = (
  groups: AiPolicyFormRuleGroup[] | undefined
) =>
  (groups ?? []).some((group) =>
    hasSensorAndPlatformRules(
      (group?.rules ?? []).map((rule) => rule?.condition)
    )
  )

const withToolArguments = (group: PolicyRecord): PolicyRecord => ({
  ...group,
  rules: group.rules.map((rule) =>
    rule.id === ARGUMENTS ? { ...rule, id: TOOL_ARGUMENTS } : rule
  )
})

// The tool-call conditions besides tool names that pick the tools a loaded
// card's access types and destinations narrow. A card only gets them so from
// the API: the editor adds access types and destinations to a Tool Access
// tool name alone
const TOOL_PICKING_RULES = [
  "toolCategories",
  "toolSources",
  "connectors",
  ARGUMENTS
]

// A card with access types or destinations but nothing that picks the tools
// they narrow: no Tool Access tool name, and no tool category, source,
// connector or argument. The whole workload's destinations narrow the workload
export const hasAccessWithoutToolName = (
  groups: AiPolicyFormRuleGroup[] | undefined
) =>
  (groups ?? []).some((group) => {
    const rules = group?.rules ?? []

    return (
      rules.some(
        (rule) => isAccessTypesRule(rule) || isDestinationRule(rule)
      ) &&
      !rules.some(isToolAccessRow) &&
      !rules.some(isWholeWorkloadRow) &&
      !rules.some((rule) => TOOL_PICKING_RULES.includes(rule?.condition ?? ""))
    )
  })

// Whether a Workload Access card has a condition left to add
export const hasWholeWorkloadConditionToAdd = (
  card: (AiPolicyFormRule | undefined)[]
) =>
  card.some(isWholeWorkloadRow) &&
  [ACCESS_TYPES_RULE, NETWORK_DESTINATION_RULE].some((id) =>
    isOfferedInCard(id, card)
  )

// The access types of a Workload Access card: those it does not take yet are
// listed, disabled
export const getWholeWorkloadAccessTypesOption = (
  t: TFunction,
  option: LiveRuleOption
): LiveRuleOption => ({
  ...option,
  options: option.options.map((item) =>
    WHOLE_WORKLOAD_ACCESS_TYPES.some((type) => type === item.value)
      ? item
      : {
          ...item,
          label: t("governance.ai.live.form.accessTypes.notForWholeWorkload", {
            type: item.label
          }),
          disabled: true
        }
  )
})

// A Workload Access card without destinations: it would deny nothing
export const isWholeWorkloadWithoutDestinations = (
  rules: (AiPolicyFormRule | undefined)[]
) => rules.some(isWholeWorkloadRow) && !rules.some(isDestinationRule)

// A Tool Access card with neither access types nor destinations: the Kodem
// sensor would have nothing to enforce
export const isToolAccessWithoutAccess = (
  rules: (AiPolicyFormRule | undefined)[]
) =>
  rules.some(isToolAccessRow) &&
  !rules.some((rule) => isAccessTypesRule(rule) || isDestinationRule(rule))

// A destination's text with its ports, when it has some
const withPorts = (t: TFunction, target: string, ports?: string) =>
  ports
    ? t("governance.ai.live.destinationPorts", { destination: target, ports })
    : target

// A destination as read: its preset's name, or its address block or host name,
// with its ports
export const getDestinationLabel = (
  t: TFunction,
  destination: AiPolicyNetworkDestination
) => {
  const { cidr, hostname, preset } = destination
  const target = preset
    ? t(`governance.ai.live.form.destinations.presets.${preset}`)
    : (cidr ?? hostname ?? "")

  return withPorts(t, target, getPortsText(destination))
}

// A destination row as the editor loads it, read: a host name and its *.
// name on the same ports are one row, the host name with its subdomains
const getFormDestinationLabel = (
  t: TFunction,
  { value = "", ports, kept, subdomains }: AiPolicyFormDestination
) => {
  if (kept) return getDestinationLabel(t, kept)

  const target = subdomains
    ? t("governance.ai.live.destinationSubdomains", { hostname: value })
    : value

  return withPorts(t, target, ports)
}

// The policies' rule cards as the live editor groups them: each card with its
// condition's access types and network destinations
export const withAccessRules = (
  records: PolicyListRecord[],
  t: TFunction
): PolicyListRecord[] =>
  records.map((record) => {
    if (record.type === "heading") return record

    const conditions = record.live?.conditions ?? []

    return {
      ...record,
      notAllowed: record.notAllowed.map((group, index) => {
        const { accessTypes = [], networkDestinations = [] } =
          conditions[index] ?? {}
        const card = withToolArguments(group)

        return {
          ...card,
          rules: [
            ...card.rules,
            ...(accessTypes.length
              ? [{ id: ACCESS_TYPES_RULE, value: accessTypes }]
              : []),
            ...(networkDestinations.length
              ? [
                  {
                    id: NETWORK_DESTINATION_RULE,
                    value: toFormDestinations(networkDestinations).map((item) =>
                      getFormDestinationLabel(t, item)
                    )
                  }
                ]
              : [])
          ]
        }
      })
    }
  })
